/** @jsx jsx */
import { jsx } from '@emotion/core'
import React, { useState, useContext, useMemo, useEffect, useRef } from 'react'
import { modifierCol, modifiedDateCol } from 'core/HeaderColTypeProps'
import { dateCol, strCol, numCol } from '@integec/grid-tools/lib/cols'
import {
  marginPnodeZone,
  marginRtPrice,
  marginRtPriceUpdate,
  marginRtPriceInsert,
  marginRtPriceDelete,
} from 'api/pgads'
import { connect } from 'react-redux'
import {
  getApiTargetInfo,
  getEnvConfig,
  getPermitActions,
  getUnits,
  hasAccesPermits,
  ExportComponent,
  exportDataComponent,
  rowHeaderRender,
  getValidYearRange,
  isValidYearRange,
} from 'core'
import {
  CriteriaRow,
  Title,
  Filler,
  Content,
  FullHeightSizer,
} from 'core/screen-util'
import {
  Button,
  DatePicker,
  // Checkbox,
  InputNumber,
  Select,
  Input,
  Modal,
  message,
} from 'antd'
import R from 'ramda'
import { ErrorContext, SpinContext } from 'context'
import * as Actions from 'core/EventActions'

import * as freeEditEngine from '@integec/grid-tools/lib/editEngine'

import Grid, { virtualizedGridRenderer } from '@integec/grid-tools'
import MarginAnalysisContext from 'marginanalysis/MarginAnalysisContext'
import { sortStringArray } from '../../core/utils'

const title = 'RT Price Data'

const RtPriceData = ({
  targetInfo,
  envConfig,
  actionPermits,
  validYearRange,
}) => {
  const GridRef = useRef()

  const { selectedDateRangePicker, setSelectedDateRagePicker } = useContext(
    MarginAnalysisContext
  )

  const { setError } = useContext(ErrorContext)
  const { setSpinning, spinning } = useContext(SpinContext)
  // eslint-disable-next-line no-unused-vars
  const [editMode, setEditMode] = useState(true)
  const [pnodeList, setPnodeList] = useState([])
  const [data, setData] = useState([])

  const [fuzzyFilter, setFuzzyFilter] = useState('')
  const [selectedPnode, setSelectedPnode] = useState('ALL')
  const [selectedRow, setSelectedRow] = useState([])

  const [editInfo, setEditInfo] = useState(
    freeEditEngine.generateInitialEditInfo()
  )
  const [showInsert, setInsert] = useState(false)
  const [insertData, setInsertData] = useState()

  const deleteData = _ => {
    Modal.confirm({
      content: 'Are you sure you want to delete data ?',
      title: 'Delete Data',
      onOk() {
        setSpinning(true)
        marginRtPriceDelete(targetInfo)(selectedRow).fork(setError, _ => {
          setSpinning(false)
          clearData()
          retrieveData()
        })
      },
      onCancel() {},
    })
  }

  const showInsertCriteria = _ => {
    if (!insertData) {
      setInsertData({
        transdate: selectedDateRangePicker[0].format('YYYY-MM-DD'),
        transdatemom: selectedDateRangePicker[0],
        pnodeid: pnodeList[0],
        hour: '1',
        lmp: 0,
      })
    }
    setInsert(true)
  }

  const insertNewData = bool => {
    setSpinning(true)
    marginRtPriceInsert(targetInfo)([insertData]).fork(setError, _ => {
      setInsert(bool)
      setSpinning(false)
      retrieveData()
    })
  }

  const updateData = _ => {
    const saveData = freeEditEngine.applyEdits({
      data: data.filter(d => d.updated),
      editInfo,
    })
    if (saveData.length <= 0) {
      message.error('Please, update data')
      return
    }

    marginRtPriceUpdate(targetInfo)(saveData).fork(setError, _ => {
      clearData()
      retrieveData()
    })
  }

  const retrieveData = _ => {
    const isValid = isValidYearRange(
      validYearRange,
      selectedDateRangePicker[0],
      selectedDateRangePicker[1]
    )
    if (!isValid) return
    const req = {
      startdate: selectedDateRangePicker[0].format('YYYY-MM-DD'),
      enddate: selectedDateRangePicker[1].format('YYYY-MM-DD'),
      // pnodes: selectedPnode === 'ALL' ? pnodeList : selectedPnode,
      pnodes: selectedPnode,
    }
    setSpinning(true)
    marginRtPrice(targetInfo)(req).fork(setError, data => {
      setSpinning(false)
      clearData()
      setData(data)
    })
  }

  const clearData = _ => {
    setEditInfo(freeEditEngine.generateInitialEditInfo())
    setData([])
    setSelectedRow([])
  }

  useEffect(
    _ => {
      marginPnodeZone(targetInfo).fork(setError, data => {
        const uniqList = R.compose(
          // R.sort((a, b) => a - b, []),
          R.uniq,
          R.map(d => d.pnodeid),
          R.sortWith([R.ascend(R.props('pnodeid'))])
        )(data)
        setPnodeList(uniqList)
        //       setSeasonMap(seasonMap)
      })
    },
    [setError, targetInfo]
  )

  const writeEventActionPermission = useMemo(
    _ =>
      hasAccesPermits({
        authGodMode: envConfig.authGodMode,
        actionPermits,
        eventActions: [
          Actions.MARGIN_ANALYSIS_ADMIN,
          Actions.MARGIN_ANALYSIS_CORE_WRITE,
        ],
        matchAny: true,
      }),
    [actionPermits, envConfig.authGodMode]
  )

  const writePermission = useMemo(_ => writeEventActionPermission && editMode, [
    writeEventActionPermission,
    editMode,
  ])

  // eslint-disable-next-line no-unused-vars
  const isEditableGrid = ({ header, rowData }) => {
    if (
      header.ident === 'modifier' ||
      header.ident === 'modifiedDate' ||
      header.isKey
    )
      return false

    return writePermission
  }

  const editRow = editInfo => {
    const modData = data.map(d => {
      if (editInfo.updatedMap.get(d)) {
        return {
          ...editInfo.updatedMap.get(d),
          // modificationInfo: { user: null, modDate: moment() },
          modificationInfo: undefined,
          updated: true,
        }
      } else {
        return d
      }
    })

    let editData = editInfo.updated
    editData = editData.map(d => ({
      ...d,
      // modificationInfo: { user: null, modDate: moment() },
      modificationInfo: undefined,
    }))
    setData(modData)
    setEditInfo({ ...editInfo, updated: editData })
  }

  return (
    <div>
      <CriteriaRow>
        <Title>{title}</Title>

        <DatePicker.RangePicker
          style={{ marginLeft: '15px', maxWidth: '250px' }}
          allowClear={false}
          format="MM/DD/YYYY"
          value={selectedDateRangePicker}
          onChange={d => {
            setSelectedDateRagePicker(d)
            // retrieve({ dates: d })
            clearData()
          }}
        />
        <div style={{ marginLeft: '10px' }}> Price Node : </div>
        <Select
          css={{ marginLeft: '10px', minWidth: '150px' }}
          value={selectedPnode}
          onChange={sel => {
            setSelectedPnode(sel)
            clearData()
          }}
        >
          <Select.Option key="ALL" value="ALL">
            ALL
          </Select.Option>
          {sortStringArray(pnodeList).map(p => (
            <Select.Option key={p} value={p}>
              {p}
            </Select.Option>
          ))}
        </Select>
        {/* <Checkbox
          css={{ marginLeft: '10px', minWidth: '100px' }}
          onChange={e => setEditMode(e.target.checked)}
          checked={editMode}
        >
          Edit Mode
        </Checkbox> */}
        <Button
          type="primary"
          icon="database"
          onClick={retrieveData}
          style={{ marginLeft: '5px' }}
          loading={spinning}
        >
          Retrieve
        </Button>
        <Button
          icon="plus-circle"
          style={{ backgroundColor: '#e6f7ff', marginLeft: '10px' }}
          loading={spinning}
          onClick={showInsertCriteria}
        >
          Insert
        </Button>
        <Button
          type="primary"
          onClick={updateData}
          style={{ marginLeft: '5px' }}
          icon="edit"
          loading={spinning}
        >
          Update
        </Button>
        <Button
          style={{ marginLeft: '5px' }}
          type="danger"
          loading={spinning}
          icon="delete"
          onClick={deleteData}
          disabled={selectedRow.length <= 0}
        >
          Delete
        </Button>

        <Filler />
        <Input.Search
          placeholder="Filter"
          value={fuzzyFilter}
          css={{
            marginLeft: '10px',
            marginRight: '10px',
            flex: '0 0 200px',
          }}
          onChange={e => setFuzzyFilter(e.target.value)}
        />
        <ExportComponent
          getData={_ => exportDataComponent(GridRef, data, fuzzyFilter)}
          data={data}
          headers={OaRtLmpHeaders || []}
          fileName={title}
          btnStyle={{ marginRight: '10px' }}
          disabled={data.length <= 0}
        />
      </CriteriaRow>
      {showInsert && (
        <CriteriaRow style={{ marginTop: '5px' }}>
          <div>Date</div>
          <DatePicker
            style={{ marginLeft: '10px' }}
            allowClear={false}
            format="MM/DD/YYYY"
            value={insertData.transdatemom}
            onChange={d => {
              setInsertData({
                ...insertData,
                transdatemom: d,
                transdate: d.format('YYYY-MM-DD'),
              })
            }}
          />
          <div style={{ marginLeft: '10px' }}>Hour</div>
          <Select
            style={{ width: '100px', marginLeft: '10px' }}
            value={insertData.hour}
            onChange={hour => setInsertData({ ...insertData, hour })}
          >
            {R.range(1, 32).map(i => (
              <Select.Option key={i} value={i}>
                {i}
              </Select.Option>
            ))}
          </Select>

          <div style={{ marginLeft: '10px' }}>PNode Id</div>
          <Select
            defaultValue={pnodeList[0]}
            value={insertData.pnodeid}
            onChange={pnodeid => setInsertData({ ...insertData, pnodeid })}
            style={{ width: '150px', marginLeft: '10px' }}
          >
            {sortStringArray(pnodeList).map(p => (
              <Select.Option key={p} value={p}>
                {p}
              </Select.Option>
            ))}
          </Select>
          <div style={{ marginLeft: '10px' }}>Price</div>
          <InputNumber
            style={{ width: '100px', marginLeft: '10px' }}
            value={insertData.lmp}
            onChange={lmp => setInsertData({ ...insertData, lmp })}
          />
          <Button
            style={{ marginLeft: '10px' }}
            type="primary"
            onClick={_ => insertNewData(true)}
            loading={spinning}
          >
            Add
          </Button>
          <Button
            style={{ marginLeft: '10px' }}
            type="primary"
            onClick={_ => insertNewData(false)}
            loading={spinning}
          >
            Add & Hide
          </Button>
          <Button
            style={{ marginLeft: '10px' }}
            type="danger"
            onClick={_ => setInsert(false)}
            loading={spinning}
          >
            Cancel
          </Button>
        </CriteriaRow>
      )}
      <FullHeightSizer heightOffset={showInsert ? 280 : 210}>
        {({ height, width }) => (
          <Content style={{ height: height - 20 }}>
            <Grid
              ref={GridRef}
              editMode="cell"
              data={data || []}
              headers={OaRtLmpHeaders}
              altBy={d => d.pnodeid}
              altBgColor="#ebebeb"
              onEditInfoChange={editInfo => editRow(editInfo)}
              editInfo={editInfo}
              render={virtualizedGridRenderer({
                autoFixColByKey: true,
                rowHeaderRender,
                height: height - 50,
                width: width - 50,
                headerRowHeight: 30,
              })}
              fuzzyFilter={fuzzyFilter}
              onSelectionChange={({ selectedRows }) => {
                setSelectedRow(selectedRows)
              }}
              initialSortOptions={OaRtLmpHeaders}
              sortOptions={OaRtLmpHeaders}
              sortEnabled
              isEditable={isEditableGrid}
              selectionType="row"
              selectionMode="single"
            />
          </Content>
        )}
      </FullHeightSizer>
    </div>
  )
}

const mapStateToProps = state => ({
  targetInfo: getApiTargetInfo(state),
  envConfig: getEnvConfig(state),
  actionPermits: getPermitActions(state),
  units: getUnits(state),
  validYearRange: getValidYearRange(state),
})

export default connect(mapStateToProps)(RtPriceData)

const OaRtLmpHeaders = [
  dateCol({ ident: 'transdate', display: 'Date', isKey: true }),
  strCol({ ident: 'pnodeid', display: 'PNode Id', isKey: true, width: 200 }),
  numCol({ ident: 'hour1', display: 'Hour 1', isFiltered: true }),
  numCol({ ident: 'hour2', display: 'Hour 2', isFiltered: true }),
  numCol({ ident: 'hour3', display: 'Hour 3', isFiltered: true }),
  numCol({ ident: 'hour4', display: 'Hour 4', isFiltered: true }),
  numCol({ ident: 'hour5', display: 'Hour 5', isFiltered: true }),
  numCol({ ident: 'hour6', display: 'Hour 6', isFiltered: true }),
  numCol({ ident: 'hour7', display: 'Hour 7', isFiltered: true }),
  numCol({ ident: 'hour8', display: 'Hour 8', isFiltered: true }),
  numCol({ ident: 'hour9', display: 'Hour 9', isFiltered: true }),
  numCol({ ident: 'hour10', display: 'Hour 10', isFiltered: true }),
  numCol({ ident: 'hour11', display: 'Hour 11', isFiltered: true }),
  numCol({ ident: 'hour12', display: 'Hour 12', isFiltered: true }),
  numCol({ ident: 'hour13', display: 'Hour 13', isFiltered: true }),
  numCol({ ident: 'hour14', display: 'Hour 14', isFiltered: true }),
  numCol({ ident: 'hour15', display: 'Hour 15', isFiltered: true }),
  numCol({ ident: 'hour16', display: 'Hour 16', isFiltered: true }),
  numCol({ ident: 'hour17', display: 'Hour 17', isFiltered: true }),
  numCol({ ident: 'hour18', display: 'Hour 18', isFiltered: true }),
  numCol({ ident: 'hour19', display: 'Hour 19', isFiltered: true }),
  numCol({ ident: 'hour20', display: 'Hour 20', isFiltered: true }),
  numCol({ ident: 'hour21', display: 'Hour 21', isFiltered: true }),
  numCol({ ident: 'hour22', display: 'Hour 22', isFiltered: true }),
  numCol({ ident: 'hour23', display: 'Hour 23', isFiltered: true }),
  numCol({ ident: 'hour24', display: 'Hour 24', isFiltered: true }),
  numCol({ ident: 'hour25', display: 'Hour 25', isFiltered: true }),
  modifierCol,
  modifiedDateCol,
]
